import { useState, useEffect, useRef, Fragment } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { calculateRefund } from '../lib/cancellation'
import { BOOKING_STATUS } from '../lib/constants'
import Header from '../components/Header'
import { prepareImageForUpload } from '../lib/imageUpload'
import { detectContactDetails, CONTACT_WARNING, CONTACT_MODE, removeContactDetails } from '../lib/contactDetection'
import { checkAndLogContact } from '../lib/contactFlags'

const CASH_KEYWORDS = ['cash', 'bank transfer', 'paypal', 'venmo', 'bpay', 'eft', 'direct deposit', 'outside', 'bypass', 'offline']
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'

const QUICK_REPLIES = {
owner_pending: [
{ text: '✅ Sounds good — I\'ll confirm shortly', instant: true },
{ text: '⏰ What time were you thinking?', instant: false },
{ text: '📅 Are you flexible on dates?', instant: false },
{ text: '❓ What will you use it for?', instant: false },
],
owner_confirmed: [
{ text: '📍 Pickup address is in my listing', instant: true },
{ text: '🔑 It\'s ready to collect anytime today', instant: true },
{ text: '💬 Send me a message 10 min before you arrive', instant: false },
{ text: '📦 All set — see you then!', instant: true },
],
hirer_initial: [
{ text: '👋 Hi! Is this available for the dates I selected?', instant: true },
{ text: '📍 Where is the pickup location?', instant: true },
{ text: '📷 Any more photos?', instant: true },
{ text: '❓ Anything else I should know?', instant: false },
],
// Messaging about a listing before any booking exists (no dates chosen yet)
hirer_enquiry: [
{ text: '👋 Hi! Is this still available?', instant: true },
{ text: '📷 Any more photos?', instant: true },
{ text: '❓ Anything else I should know?', instant: false },
],
hirer_paid: [
{ text: '💳 Just paid — see you soon!', instant: true },
{ text: '⏰ Running late, be there shortly', instant: true },
{ text: '📦 On my way to collect', instant: true },
{ text: '🙏 Thanks heaps!', instant: true },
],
universal: [
{ text: '👍 Thanks!', instant: true },
{ text: '👌 Sounds good', instant: true },
{ text: '🙏 Cheers', instant: true },
{ text: '✅ Yes', instant: true },
{ text: '❌ No', instant: true },
],
}

export default function Messages() {
const { user, profile } = useAuth()
const navigate = useNavigate()
const [searchParams] = useSearchParams()
const [conversations, setConversations] = useState([])
const [activeConvo, setActiveConvo] = useState(null)
const [messages, setMessages] = useState([])
const [newMessage, setNewMessage] = useState('')
const [loading, setLoading] = useState(true)
const [sending, setSending] = useState(false)
const [reportModal, setReportModal] = useState(false)
const [reportReason, setReportReason] = useState('')
const [blockConfirm, setBlockConfirm] = useState(false)
const [blockedUsers, setBlockedUsers] = useState([])
const [showPaymentWarning, setShowPaymentWarning] = useState(false)
const [showContactWarning, setShowContactWarning] = useState(false)
const [payProcessing, setPayProcessing] = useState(false)
const [payError, setPayError] = useState('')
const [photoUploading, setPhotoUploading] = useState(false)
const [photoError, setPhotoError] = useState('')
const [pickupCondition, setPickupCondition] = useState('')
const [returnCondition, setReturnCondition] = useState('')
const [ownerReturnNotes, setOwnerReturnNotes] = useState('')
const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
const [otherTyping, setOtherTyping] = useState(false)
const [cancelModal, setCancelModal] = useState(false)
const [cancelReason, setCancelReason] = useState('')
const [cancelling, setCancelling] = useState(false)
const messagesEndRef = useRef(null)
const subscriptionRef = useRef(null)
const unreadSubRef = useRef(null)
const typingChannelRef = useRef(null)
const typingTimeoutRef = useRef(null)
const lastTypingSentRef = useRef(0)
const inputRef = useRef(null)
const audioRef = useRef(null)
const pickupPhotoRef = useRef(null)
const returnPhotoRef = useRef(null)
const ownerPickupPhotoRef = useRef(null)
const ownerReturnPhotoRef = useRef(null)
const chatPhotoRef = useRef(null)

useEffect(() => {
audioRef.current = new Audio('https://cdn.jsdelivr.net/gh/loop4less/sounds@main/notification-soft.mp3')
audioRef.current.volume = 0.4
}, [])

useEffect(() => {
const handleResize = () => setIsMobile(window.innerWidth < 768)
window.addEventListener('resize', handleResize)
return () => window.removeEventListener('resize', handleResize)
}, [])

useEffect(() => {
if (!user) return // ProtectedRoute sends logged-out visitors to sign-in
fetchConversations()
fetchBlockedUsers()
subscribeToUnread()
return () => {
if (subscriptionRef.current) subscriptionRef.current.unsubscribe()
if (unreadSubRef.current) unreadSubRef.current.unsubscribe()
if (typingChannelRef.current) typingChannelRef.current.unsubscribe()
}
}, [user])

useEffect(() => {
if (activeConvo) {
fetchMessages(activeConvo.id)
subscribeToMessages(activeConvo.id)
subscribeToTyping(activeConvo.id)
setConversations(prev => prev.map(c => c.id === activeConvo.id ? { ...c, unreadCount: 0 } : c))
setOtherTyping(false)
setTimeout(() => inputRef.current?.focus(), 100)
}
return () => {
if (subscriptionRef.current) subscriptionRef.current.unsubscribe()
if (typingChannelRef.current) typingChannelRef.current.unsubscribe()
}
}, [activeConvo?.id])

useEffect(() => {
messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
}, [messages, otherTyping])

const playPing = () => {
try {
if (audioRef.current) {
audioRef.current.currentTime = 0
audioRef.current.play().catch(() => {})
}
} catch (e) {}
}

const checkForCashKeywords = (text) => {
const lower = text.toLowerCase()
return CASH_KEYWORDS.some(keyword => lower.includes(keyword))
}

const fetchBlockedUsers = async () => {
const { data } = await supabase.from('blocked_users').select('blocked_id').eq('blocker_id', user.id)
setBlockedUsers(data?.map(b => b.blocked_id) || [])
}

const subscribeToUnread = () => {
if (unreadSubRef.current) unreadSubRef.current.unsubscribe()
unreadSubRef.current = supabase
.channel('unread-messages')
.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, () => {
fetchConversations()
})
.subscribe()
}

const subscribeToMessages = (bookingId) => {
if (subscriptionRef.current) subscriptionRef.current.unsubscribe()
subscriptionRef.current = supabase
.channel(`messages:${bookingId}`)
.on('postgres_changes', {
event: 'INSERT', schema: 'public', table: 'messages',
filter: `booking_id=eq.${bookingId}`
}, (payload) => {
setMessages(prev => {
const exists = prev.find(m => m.id === payload.new.id)
if (exists) return prev
if (payload.new.sender_id !== user.id) {
playPing()
}
return [...prev, payload.new]
})
})
.on('postgres_changes', {
event: 'UPDATE', schema: 'public', table: 'messages',
filter: `booking_id=eq.${bookingId}`
}, (payload) => {
setMessages(prev => prev.map(m => m.id === payload.new.id ? { ...m, read: payload.new.read } : m))
})
.subscribe()
}

const subscribeToTyping = (bookingId) => {
if (typingChannelRef.current) typingChannelRef.current.unsubscribe()
typingChannelRef.current = supabase
.channel(`typing:${bookingId}`)
.on('broadcast', { event: 'typing' }, (payload) => {
if (payload.payload?.userId && payload.payload.userId !== user.id) {
setOtherTyping(true)
if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
typingTimeoutRef.current = setTimeout(() => setOtherTyping(false), 3000)
}
})
.subscribe()
}

const broadcastTyping = () => {
const now = Date.now()
if (now - lastTypingSentRef.current < 1500) return
lastTypingSentRef.current = now
if (typingChannelRef.current) {
typingChannelRef.current.send({
type: 'broadcast',
event: 'typing',
payload: { userId: user.id },
})
}
}

const fetchConversations = async () => {
setLoading(true)
let { data } = await supabase
.from('bookings')
.select(`
id, status, hirer_id, owner_id, total_amount, deposit_amount,
paid_at, picked_up_at, returned_at, stripe_payment_intent_id,
pickup_photo_url, return_photo_url, pickup_condition, return_condition,
owner_pickup_photo_url, owner_return_photo_url,
owner_pickup_agreed, owner_return_agreed,
start_date, hours, cancelled_at, cancelled_by, cancellation_reason, refund_owed,
listings(title, images, requires_id)
`)
.or(`hirer_id.eq.${user.id},owner_id.eq.${user.id}`)
.order('created_at', { ascending: false })
const _cids = [...new Set((data || []).flatMap(b => [b.hirer_id, b.owner_id]).filter(Boolean))]
let _cmap = {}
if (_cids.length) {
const { data: _cp } = await supabase.from('public_profiles').select('id, full_name, stripe_account_id, stripe_charges_enabled').in('id', _cids)
;(_cp || []).forEach(pp => { _cmap[pp.id] = pp })
}
data = (data || []).map(b => ({ ...b, hirer: _cmap[b.hirer_id] || null, owner: _cmap[b.owner_id] || null }))

if (data) {
const convosWithUnread = await Promise.all(data.map(async (convo) => {
const { count } = await supabase
.from('messages').select('*', { count: 'exact', head: true })
.eq('booking_id', convo.id).eq('read', false).neq('sender_id', user.id)
return { ...convo, unreadCount: count || 0 }
}))
setConversations(convosWithUnread)

if (activeConvo) {
const refreshed = convosWithUnread.find(c => c.id === activeConvo.id)
if (refreshed) setActiveConvo(refreshed)
}
}

setLoading(false)

const bookingId = searchParams.get('booking')
if (bookingId && data && !activeConvo) {
const convo = data.find(c => c.id === bookingId)
if (convo) setActiveConvo(convo)
}
}

const fetchMessages = async (bookingId) => {
let { data } = await supabase
.from('messages')
.select('*')
.eq('booking_id', bookingId)
.order('created_at', { ascending: true })
const _sids = [...new Set((data || []).map(m => m.sender_id).filter(Boolean))]
let _smap = {}
if (_sids.length) {
const { data: _sp } = await supabase.from('public_profiles').select('id, full_name').in('id', _sids)
;(_sp || []).forEach(pp => { _smap[pp.id] = pp })
}
data = (data || []).map(m => ({ ...m, sender: _smap[m.sender_id] || null }))
setMessages(data || [])
await supabase.from('messages').update({ read: true }).eq('booking_id', bookingId).neq('sender_id', user.id)
}

const sendEmailNotification = async (messageBody) => {
try {
const recipient = getOtherPerson(activeConvo)
if (!recipient?.id) return
const { data: recipientEmail } = await supabase.rpc('get_user_email', { user_id: recipient.id })
if (!recipientEmail) return
const { data: { session } } = await supabase.auth.getSession()
fetch(`${BACKEND_URL}/notify-message`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
recipientEmail,
recipientName: recipient.full_name,
senderName: profile?.full_name || 'A HireIt user',
itemTitle: activeConvo.listings?.title || 'your booking',
messagePreview: messageBody,
bookingId: activeConvo.id,
})
}).catch(e => console.log('Message email failed:', e))
} catch (e) {
console.log('Message notify failed:', e)
}
}

const sendMessageDirect = async (messageBody) => {
if (!messageBody.trim() || !activeConvo) return
const otherPerson = getOtherPerson(activeConvo)
if (blockedUsers.includes(otherPerson?.id)) {
alert('You have blocked this user.')
return
}
setSending(true)
const tempMsg = {
id: `temp-${Date.now()}`,
booking_id: activeConvo.id,
sender_id: user.id,
body: messageBody.trim(),
read: false,
created_at: new Date().toISOString(),
sender: { full_name: 'You' }
}
setMessages(prev => [...prev, tempMsg])
const { data } = await supabase.from('messages').insert({
booking_id: activeConvo.id,
sender_id: user.id,
body: messageBody.trim(),
read: false,
}).select('*').single()
if (data) {
setMessages(prev => prev.map(m => m.id === tempMsg.id ? { ...data, sender: { full_name: profile?.full_name || 'You' } } : m))
}
sendEmailNotification(messageBody.trim())
setSending(false)
}

// Contact details are fine once the booking is paid and confirmed
const contactDetailsAllowed = (convo) => !!convo?.paid_at && !['enquiry', 'pending', 'cancelled'].includes(convo?.status)

// Contact-details check (after the payment check). Warning mode: show the
// notice and log it, then let them send anyway. Block mode (new Terms):
// details are swapped for [contact removed] and the message doesn't send.
const sendAfterChecks = async (skipContact = false) => {
let msgToSend = newMessage.trim()
if (!skipContact && !contactDetailsAllowed(activeConvo) && detectContactDetails(msgToSend).found) {
checkAndLogContact('message', msgToSend, activeConvo.id)
if (CONTACT_MODE === 'block') { setNewMessage(removeContactDetails(msgToSend)); setShowContactWarning(true); return }
setShowContactWarning(true)
return
}
setNewMessage('')
await sendMessageDirect(msgToSend)
}

const sendMessage = async () => {
if (!newMessage.trim() || !activeConvo) return
if (checkForCashKeywords(newMessage)) {
setShowPaymentWarning(true)
return
}
await sendAfterChecks()
}

const sendMessageAnyway = async () => {
setShowPaymentWarning(false)
await sendAfterChecks()
}

const sendContactAnyway = async () => {
setShowContactWarning(false)
await sendAfterChecks(true)
}

const handleInputChange = (e) => {
setNewMessage(e.target.value)
if (e.target.value.trim()) {
broadcastTyping()
}
}

const handleQuickReply = (reply) => {
if (reply.instant) {
sendMessageDirect(reply.text)
} else {
setNewMessage(reply.text + ' ')
inputRef.current?.focus()
}
}

const getQuickReplies = () => {
if (!activeConvo) return []
const iAmOwner = activeConvo.owner_id === user.id
const iAmHirer = activeConvo.hirer_id === user.id
const status = activeConvo.status
const isPaid = !!activeConvo.paid_at

let contextual = []

if (iAmOwner) {
if (status === 'pending') contextual = QUICK_REPLIES.owner_pending
else if (status === 'confirmed') contextual = QUICK_REPLIES.owner_confirmed
} else if (iAmHirer) {
// 'enquiry' = just messages, no booking: a direct message from a profile
// (no listing) gets only the general replies
if (status === 'enquiry') contextual = activeConvo.listing_id ? QUICK_REPLIES.hirer_enquiry : []
else if (status === 'pending') contextual = QUICK_REPLIES.hirer_initial
else if (isPaid) contextual = QUICK_REPLIES.hirer_paid
else if (status === 'confirmed') contextual = QUICK_REPLIES.hirer_initial
}

return [...contextual, ...QUICK_REPLIES.universal]
}

const uploadBookingPhoto = async (file, type) => {
const fileToUpload = await prepareImageForUpload(file)
const fileName = `${activeConvo.id}-${type}-${Date.now()}.jpg`
const { error: uploadError } = await supabase.storage
.from('booking-photos')
.upload(fileName, fileToUpload, { cacheControl: '31536000', upsert: false, contentType: 'image/jpeg' })
if (uploadError) throw uploadError
const { data: { publicUrl } } = supabase.storage.from('booking-photos').getPublicUrl(fileName)
return publicUrl
}

const handleChatPhotoUpload = async (e) => {
const file = e.target.files?.[0]
if (!file || !activeConvo) return
const otherPerson = getOtherPerson(activeConvo)
if (blockedUsers.includes(otherPerson?.id)) return
setPhotoUploading(true)
setPhotoError('')
try {
const photoUrl = await uploadBookingPhoto(file, 'chat')
await supabase.from('messages').insert({
booking_id: activeConvo.id,
sender_id: user.id,
body: `📷 Photo\n${photoUrl}`,
read: false,
})
sendEmailNotification('Sent you a photo 📷')
if (chatPhotoRef.current) chatPhotoRef.current.value = ''
} catch (e) {
setPhotoError('Failed to send photo: ' + e.message)
}
setPhotoUploading(false)
}

const handlePickupPhotoUpload = async (e) => {
const file = e.target.files?.[0]
if (!file || !activeConvo) return
if (!pickupCondition) {
setPhotoError('Please select item condition first.')
return
}
setPhotoUploading(true)
setPhotoError('')
try {
const photoUrl = await uploadBookingPhoto(file, 'pickup')
const pickedUpAt = new Date().toISOString()
await supabase.from('bookings').update({
pickup_photo_url: photoUrl,
pickup_condition: pickupCondition,
picked_up_at: pickedUpAt,
}).eq('id', activeConvo.id)

await supabase.from('messages').insert({
booking_id: activeConvo.id,
sender_id: user.id,
body: `📸 Pickup photo uploaded — condition recorded as: **${pickupCondition}**\n${photoUrl}`,
read: false,
})

if (pickupPhotoRef.current) pickupPhotoRef.current.value = ''

setActiveConvo(prev => ({
...prev,
pickup_photo_url: photoUrl,
pickup_condition: pickupCondition,
picked_up_at: pickedUpAt,
}))
setPickupCondition('')

try {
const { data: ownerEmail } = await supabase.rpc('get_user_email', { user_id: activeConvo.owner_id })
const { data: { session } } = await supabase.auth.getSession()
await fetch(`${BACKEND_URL}/notify-pickup`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
ownerEmail,
ownerName: activeConvo.owner?.full_name,
hirerName: profile?.full_name || user.email,
itemTitle: activeConvo.listings?.title,
})
})
} catch (e) {}
} catch (e) {
setPhotoError('Failed to upload photo: ' + e.message)
}
setPhotoUploading(false)
}

const handleReturnPhotoUpload = async (e) => {
const file = e.target.files?.[0]
if (!file || !activeConvo) return
if (!returnCondition) {
setPhotoError('Please select item condition first.')
return
}
setPhotoUploading(true)
setPhotoError('')
try {
const photoUrl = await uploadBookingPhoto(file, 'return')
const returnedAt = new Date().toISOString()
await supabase.from('bookings').update({
return_photo_url: photoUrl,
return_condition: returnCondition,
returned_at: returnedAt,
}).eq('id', activeConvo.id)

await supabase.from('messages').insert({
booking_id: activeConvo.id,
sender_id: user.id,
body: `📸 Return photo uploaded — condition recorded as: **${returnCondition}**. Waiting for owner to confirm.\n${photoUrl}`,
read: false,
})

if (returnPhotoRef.current) returnPhotoRef.current.value = ''

setActiveConvo(prev => ({
...prev,
return_photo_url: photoUrl,
return_condition: returnCondition,
returned_at: returnedAt,
}))
setReturnCondition('')
} catch (e) {
setPhotoError('Failed to upload photo: ' + e.message)
}
setPhotoUploading(false)
}

const handleOwnerPickupPhotoUpload = async (e) => {
const file = e.target.files?.[0]
if (!file || !activeConvo) return
setPhotoUploading(true)
setPhotoError('')
try {
const photoUrl = await uploadBookingPhoto(file, 'owner-pickup')
await supabase.from('bookings').update({
owner_pickup_photo_url: photoUrl,
owner_pickup_agreed: true,
}).eq('id', activeConvo.id)
await supabase.from('messages').insert({
booking_id: activeConvo.id,
sender_id: user.id,
body: `📸 Owner pickup confirmation photo uploaded ✓\n${photoUrl}`,
read: false,
})

if (ownerPickupPhotoRef.current) ownerPickupPhotoRef.current.value = ''

setActiveConvo(prev => ({
...prev,
owner_pickup_photo_url: photoUrl,
owner_pickup_agreed: true,
}))
} catch (e) {
setPhotoError('Failed to upload photo: ' + e.message)
}
setPhotoUploading(false)
}

const handleOwnerPickupSkip = async () => {
if (!activeConvo) return
setPhotoUploading(true)
try {
await supabase.from('bookings').update({
owner_pickup_agreed: true,
}).eq('id', activeConvo.id)
setActiveConvo(prev => ({ ...prev, owner_pickup_agreed: true }))
} catch (e) {}
setPhotoUploading(false)
}

const handleOwnerReturnAgree = async (agreed) => {
if (!activeConvo) return
setPhotoUploading(true)
setPhotoError('')
try {
// If owner AGREES: capture hire amount only (deposit auto-released)
if (agreed && activeConvo.stripe_payment_intent_id) {
const hireAmount = (activeConvo.total_amount || 0) - (activeConvo.deposit_amount || 0)
try {
const { data: { session } } = await supabase.auth.getSession()
const captureRes = await fetch(`${BACKEND_URL}/stripe/partial-capture`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
paymentIntentId: activeConvo.stripe_payment_intent_id,
amountToCaptureAUD: hireAmount,
})
})
const captureData = await captureRes.json()
if (!captureRes.ok) {
console.error('Capture failed:', captureData)
setPhotoError('Could not release funds: ' + (captureData.error || 'unknown error'))
setPhotoUploading(false)
return
}
console.log('Captured:', captureData)
} catch (e) {
console.error('Capture error:', e)
setPhotoError('Could not release funds. Please contact support.')
setPhotoUploading(false)
return
}
}

await supabase.from('bookings').update({
owner_return_agreed: agreed,
owner_return_notes: ownerReturnNotes.trim() || null,
status: agreed ? 'completed' : 'confirmed',
payment_captured_at: agreed ? new Date().toISOString() : null,
}).eq('id', activeConvo.id)

const hireAmount = (activeConvo.total_amount || 0) - (activeConvo.deposit_amount || 0)
const msgBody = agreed
? `✅ Owner confirmed item returned in agreed condition. $${hireAmount.toFixed(2)} released to owner. $${(activeConvo.deposit_amount || 0).toFixed(2)} deposit released back to hirer. Booking complete!${ownerReturnNotes.trim() ? '\n\nNote: ' + ownerReturnNotes.trim() : ''}`
: `⚠️ Owner disagrees with hirer's return condition. Payment remains on hold while HireIt team reviews.${ownerReturnNotes.trim() ? '\n\nOwner note: ' + ownerReturnNotes.trim() : ''}`

await supabase.from('messages').insert({
booking_id: activeConvo.id,
sender_id: user.id,
body: msgBody,
read: false,
})

setActiveConvo(prev => ({
...prev,
owner_return_agreed: agreed,
owner_return_notes: ownerReturnNotes.trim() || null,
status: agreed ? 'completed' : 'confirmed',
}))
setOwnerReturnNotes('')
} catch (e) {
setPhotoError('Failed: ' + e.message)
}
setPhotoUploading(false)
}

const handleOwnerReturnPhotoUpload = async (e) => {
const file = e.target.files?.[0]
if (!file || !activeConvo) return
setPhotoUploading(true)
setPhotoError('')
try {
const photoUrl = await uploadBookingPhoto(file, 'owner-return')
await supabase.from('bookings').update({
owner_return_photo_url: photoUrl,
}).eq('id', activeConvo.id)
await supabase.from('messages').insert({
booking_id: activeConvo.id,
sender_id: user.id,
body: `📸 Owner return photo uploaded ✓\n${photoUrl}`,
read: false,
})

if (ownerReturnPhotoRef.current) ownerReturnPhotoRef.current.value = ''

setActiveConvo(prev => ({
...prev,
owner_return_photo_url: photoUrl,
}))
} catch (e) {
setPhotoError('Failed to upload photo: ' + e.message)
}
setPhotoUploading(false)
}

const handlePayNow = async () => {
if (!activeConvo) return
setPayProcessing(true)
setPayError('')
try {
const hireEnd = new Date(activeConvo.start_date)
hireEnd.setDate(hireEnd.getDate() + Math.ceil((activeConvo.hours || 0) / 24))
const daysToEnd = Math.ceil((hireEnd - new Date()) / (1000 * 60 * 60 * 24))
if (daysToEnd > 5) {
const earliest = new Date(hireEnd)
earliest.setDate(earliest.getDate() - 5)
setPayError(`Payment can't be taken this far ahead — the hold on your card would expire before your hire ends. You can pay from ${earliest.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}.`)
setPayProcessing(false)
return
}
const ownerStripeId = activeConvo.owner?.stripe_account_id
const ownerStripeReady = !!activeConvo.owner?.stripe_charges_enabled
if (!ownerStripeId || !ownerStripeReady) {
setPayError("The owner hasn't finished setting up payments yet. Please message them.")
setPayProcessing(false)
return
}
const { data: { session } } = await supabase.auth.getSession()
const res = await fetch(`${BACKEND_URL}/stripe/payment-intent`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
amountAUD: activeConvo.total_amount,
depositAUD: activeConvo.deposit_amount || 0,
ownerStripeId,
bookingId: activeConvo.id,
listingTitle: activeConvo.listings?.title,
})
})
const data = await res.json()
if (data.clientSecret) {
window.location.href = `/payment?booking=${activeConvo.id}&secret=${data.clientSecret}`
} else {
setPayError('Payment setup failed. Please try again.')
}
} catch (e) {
setPayError('Payment error. Please try again.')
}
setPayProcessing(false)
}

const handleCancelBooking = async () => {
if (!activeConvo) return
setCancelling(true)
try {
const isHirerCancelling = activeConvo.hirer_id === user.id
const { refundAmount, feeAmount, policy } = calculateRefund(activeConvo, isHirerCancelling)
const cancelledByLabel = isHirerCancelling ? 'hirer' : 'owner'

// Handle Stripe payment based on cancellation policy
if (activeConvo.stripe_payment_intent_id) {
try {
const { data: { session } } = await supabase.auth.getSession()
if (policy === 'unpaid' || policy === 'owner-cancel' || policy === '48+hr') {
// Full release - cancel the authorisation
const res = await fetch(`${BACKEND_URL}/stripe/cancel-payment`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({ paymentIntentId: activeConvo.stripe_payment_intent_id })
})
const data = await res.json().catch(() => ({}))
if (res.ok) {
console.log('Stripe authorisation cancelled')
} else if (data?.code === 'payment_intent_unexpected_state' && data?.paymentIntentStatus === 'canceled') {
// PI was already cancelled (webhook or prior action beat us to it) — safe to proceed
} else if (data?.code === 'payment_intent_unexpected_state') {
setCancelling(false)
alert("This booking can't be cancelled — the payment has already been processed. Please contact support at hello@hireitnow.au.")
return
} else {
console.error('Cancel failed:', data)
setCancelling(false)
alert('Could not cancel the booking. Please try again or contact support.')
return
}
} else if (policy === '24-48hr' || policy === 'under-24hr') {
// Partial capture - keep the cancellation fee
const res = await fetch(`${BACKEND_URL}/stripe/partial-capture`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
paymentIntentId: activeConvo.stripe_payment_intent_id,
amountToCaptureAUD: feeAmount,
})
})
const data = await res.json().catch(() => ({}))
if (res.ok) {
console.log('Partial capture, fee retained:', feeAmount)
} else if (data?.code === 'payment_intent_unexpected_state' && data?.paymentIntentStatus === 'canceled') {
// PI was already cancelled — no fee captured, but safe to proceed
} else if (data?.code === 'payment_intent_unexpected_state') {
setCancelling(false)
alert("This booking can't be cancelled — the payment has already been processed. Please contact support at hello@hireitnow.au.")
return
} else {
console.error('Partial capture failed:', data)
setCancelling(false)
alert('Could not process the cancellation fee. Please try again or contact support.')
return
}
}
} catch (e) {
console.error('Stripe action error:', e)
// Continue anyway - manual processing in Stripe Dashboard
}
}

await supabase.from('bookings').update({
status: 'cancelled',
cancelled_at: new Date().toISOString(),
cancelled_by: cancelledByLabel,
cancellation_reason: cancelReason.trim() || null,
refund_owed: refundAmount > 0 ? refundAmount : null,
}).eq('id', activeConvo.id)

let msgBody = ''
if (policy === 'unpaid') {
msgBody = `❌ Booking cancelled by ${cancelledByLabel}.${cancelReason.trim() ? `\n\nReason: ${cancelReason.trim()}` : ''}`
} else if (policy === 'owner-cancel') {
msgBody = `❌ Booking cancelled by owner. Full refund of $${refundAmount.toFixed(2)} will be processed within 3-5 business days.${cancelReason.trim() ? `\n\nReason: ${cancelReason.trim()}` : ''}`
} else if (policy === '48+hr') {
msgBody = `❌ Booking cancelled by hirer (48+ hours before start). Full refund of $${refundAmount.toFixed(2)} will be processed within 3-5 business days.${cancelReason.trim() ? `\n\nReason: ${cancelReason.trim()}` : ''}`
} else if (policy === '24-48hr') {
msgBody = `❌ Booking cancelled by hirer (24-48 hours before start). 50% refund of $${refundAmount.toFixed(2)} will be processed. $${feeAmount.toFixed(2)} cancellation fee retained per policy.${cancelReason.trim() ? `\n\nReason: ${cancelReason.trim()}` : ''}`
} else if (policy === 'under-24hr') {
msgBody = `❌ Booking cancelled by hirer (under 24 hours before start). No refund — full cancellation fee of $${feeAmount.toFixed(2)} retained per policy.${cancelReason.trim() ? `\n\nReason: ${cancelReason.trim()}` : ''}`
}

await supabase.from('messages').insert({
booking_id: activeConvo.id,
sender_id: user.id,
body: msgBody,
read: false,
})

try {
const otherPerson = getOtherPerson(activeConvo)
if (otherPerson?.id) {
const { data: otherEmail } = await supabase.rpc('get_user_email', { user_id: otherPerson.id })
if (otherEmail) {
const { data: { session } } = await supabase.auth.getSession()
await fetch(`${BACKEND_URL}/notify-message`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
body: JSON.stringify({
recipientEmail: otherEmail,
recipientName: otherPerson.full_name,
senderName: profile?.full_name || 'A HireIt user',
itemTitle: activeConvo.listings?.title || 'your booking',
messagePreview: `Booking cancelled by ${cancelledByLabel}`,
bookingId: activeConvo.id,
})
})
}
}
} catch (e) {}

setActiveConvo(prev => ({
...prev,
status: 'cancelled',
cancelled_at: new Date().toISOString(),
cancelled_by: cancelledByLabel,
cancellation_reason: cancelReason.trim() || null,
refund_owed: refundAmount > 0 ? refundAmount : null,
}))
setCancelModal(false)
setCancelReason('')
} catch (e) {
console.error('Cancel error:', e)
alert('Failed to cancel booking. Please try again.')
}
setCancelling(false)
}

const handleBlock = async () => {
const otherPerson = getOtherPerson(activeConvo)
if (!otherPerson) return
await supabase.from('blocked_users').insert({ blocker_id: user.id, blocked_id: otherPerson.id })
setBlockedUsers(prev => [...prev, otherPerson.id])
setBlockConfirm(false)
alert(`${otherPerson.full_name} has been blocked.`)
}

const handleUnblock = async () => {
const otherPerson = getOtherPerson(activeConvo)
if (!otherPerson) return
await supabase.from('blocked_users').delete().eq('blocker_id', user.id).eq('blocked_id', otherPerson.id)
setBlockedUsers(prev => prev.filter(id => id !== otherPerson.id))
}

const handleReport = async () => {
if (!reportReason.trim()) return
const otherPerson = getOtherPerson(activeConvo)
await supabase.from('reports').insert({
reporter_id: user.id,
reported_id: otherPerson?.id,
reason: reportReason.trim(),
booking_id: activeConvo.id,
})
setReportModal(false)
setReportReason('')
alert('Report submitted. Our team will review it shortly.')
}

const getOtherPerson = (convo) => {
if (!convo) return null
return convo.hirer?.id === user.id ? convo.owner : convo.hirer
}

const isOwner = (convo) => convo?.owner_id === user.id
const isHirer = (convo) => convo?.hirer_id === user.id

const myMessages = messages.filter(m => m.sender_id === user.id && !String(m.id).startsWith('temp-'))
const latestMyMessageId = myMessages.length > 0 ? myMessages[myMessages.length - 1].id : null

if (loading) return <div><Header /><div className="spinner-wrap"><div className="spinner" /></div></div>

const otherPerson = activeConvo ? getOtherPerson(activeConvo) : null
const isBlocked = otherPerson ? blockedUsers.includes(otherPerson.id) : false
const showList = !isMobile || !activeConvo
const showChat = !isMobile || activeConvo

const showPayNow = activeConvo && isHirer(activeConvo) &&
activeConvo.status === 'confirmed' && !activeConvo.paid_at && !activeConvo.stripe_payment_intent_id

const showHirerPickupChecklist = activeConvo && isHirer(activeConvo) &&
activeConvo.status === 'confirmed' && (activeConvo.paid_at || activeConvo.stripe_payment_intent_id) &&
!activeConvo.picked_up_at

const showOwnerPickupVerify = activeConvo && isOwner(activeConvo) &&
activeConvo.picked_up_at && !activeConvo.owner_pickup_agreed

const showHirerReturnChecklist = activeConvo && isHirer(activeConvo) &&
activeConvo.picked_up_at && !activeConvo.returned_at

const showOwnerReturnVerify = activeConvo && isOwner(activeConvo) &&
activeConvo.returned_at && activeConvo.owner_return_agreed === null

// Only real bookings can be cancelled — not message-only conversations ('enquiry')
const showCancelButton = activeConvo &&
['pending', 'confirmed'].includes(activeConvo.status) &&
!activeConvo.picked_up_at &&
!activeConvo.cancelled_at

const quickReplies = getQuickReplies()
const showQuickReplies = activeConvo && !isBlocked && !newMessage.trim() && quickReplies.length > 0 && !activeConvo.cancelled_at

const refundPreview = activeConvo ? calculateRefund(activeConvo, isHirer(activeConvo)) : null

const conditionOptionStyle = (selected, color) => ({
flex: 1,
padding: '8px 10px',
borderRadius: 8,
border: `2px solid ${selected ? color : 'var(--border)'}`,
background: selected ? `${color}15` : 'white',
cursor: 'pointer',
fontSize: 12,
fontWeight: 600,
color: selected ? color : 'var(--text-muted)',
textAlign: 'center',
transition: 'all 0.15s',
})

return (
<div>
<Header />
<div className="container" style={{ maxWidth: 900, padding: '1.5rem 1rem' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' }}>
{isMobile && activeConvo ? (
<button className="btn btn-ghost btn-sm" onClick={() => setActiveConvo(null)}>← Back</button>
) : (
<button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← Back</button>
)}
<h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)' }}>
{isMobile && activeConvo ? otherPerson?.full_name : 'Messages'}
</h1>
</div>

<div style={{
display: 'grid',
gridTemplateColumns: isMobile ? '1fr' : '300px 1fr',
gap: '1rem',
height: isMobile ? 'calc(100vh - 180px)' : '70vh'
}}>

{showList && (
<div style={{ background: 'white', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
<div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>
Conversations ({conversations.length})
</div>
<div style={{ overflowY: 'auto', flex: 1 }}>
{conversations.length === 0 ? (
<div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
No conversations yet
</div>
) : (
conversations.map(convo => {
const other = getOtherPerson(convo)
const isActive = activeConvo?.id === convo.id
return (
<div key={convo.id} onClick={() => setActiveConvo(convo)}
style={{ padding: '12px 16px', cursor: 'pointer', background: isActive ? 'var(--accent-light)' : 'white', borderBottom: '1px solid var(--border)', transition: 'background 0.15s' }}>
<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
<div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: 'var(--primary)', flexShrink: 0 }}>
{other?.full_name?.[0]?.toUpperCase() || '?'}
</div>
<div style={{ flex: 1, minWidth: 0 }}>
<div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--navy)' }}>
{other?.full_name || 'Unknown'}
{blockedUsers.includes(other?.id) && <span style={{ fontSize: 10, color: 'var(--red)', marginLeft: 6 }}>Blocked</span>}
</div>
<div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
{convo.listings?.title || 'Direct message'}
</div>
</div>
<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
{convo.status !== 'enquiry' && (
<span className={`badge badge-${convo.status === 'confirmed' ? 'primary' : convo.status === 'pending' ? 'amber' : convo.status === 'cancelled' ? 'gray' : 'gray'}`} style={{ fontSize: 10 }}>
{BOOKING_STATUS[convo.status]?.label}
</span>
)}
{convo.unreadCount > 0 && (
<span style={{ background: '#A32D2D', color: 'white', borderRadius: '99px', fontSize: 10, fontWeight: 700, padding: '2px 6px', minWidth: 18, textAlign: 'center' }}>
{convo.unreadCount}
</span>
)}
</div>
</div>
</div>
)
})
)}
</div>
</div>
)}

{showChat && (
<div style={{ background: 'white', borderRadius: 12, border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
{!activeConvo ? (
<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>
<div style={{ textAlign: 'center' }}>
<div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
<p>Select a conversation to start messaging</p>
</div>
</div>
) : (
<>
<div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
<div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: 'var(--primary)', flexShrink: 0 }}>
{otherPerson?.full_name?.[0]?.toUpperCase() || '?'}
</div>
<div style={{ flex: 1, minWidth: 0 }}>
<div style={{ fontWeight: 600, color: 'var(--navy)' }}>{otherPerson?.full_name}</div>
<div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
{isOwner(activeConvo) ? 'Hirer' : 'Owner'} · {activeConvo.listings?.title || 'Direct message'}
</div>
</div>
</div>

<div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

{activeConvo.cancelled_at && (
<div style={{ margin: '8px 16px', background: '#FEE2E2', border: '1px solid #DC2626', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#991B1B', flexShrink: 0 }}>
❌ <strong>Booking cancelled</strong> by {activeConvo.cancelled_by} on {new Date(activeConvo.cancelled_at).toLocaleDateString('en-AU')}
{activeConvo.refund_owed && <div style={{ marginTop: 4 }}>💰 Refund owed: ${activeConvo.refund_owed.toFixed(2)}</div>}
{activeConvo.cancellation_reason && <div style={{ marginTop: 4, fontStyle: 'italic' }}>"{activeConvo.cancellation_reason}"</div>}
</div>
)}

<div className="alert alert-warn" style={{ margin: '8px 16px', fontSize: 12, flexShrink: 0 }}>
🔒 For your protection, all payments must be made through HireIt. Off-platform payments are not covered by dispute resolution.
</div>

{showPayNow && (
<div style={{ margin: '8px 16px', background: 'var(--primary-light)', border: '2px solid var(--primary)', borderRadius: 12, padding: '12px 16px', flexShrink: 0 }}>
<div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--primary)' }}>💳 Payment required</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
Your booking is confirmed! Complete payment to secure your hire.
</div>
{payError && <div className="alert alert-error" style={{ marginBottom: 8, fontSize: 12 }}>{payError}</div>}
<button className="btn btn-gradient btn-full" onClick={handlePayNow} disabled={payProcessing}>
{payProcessing ? 'Processing...' : `💳 Pay now — $${activeConvo.total_amount?.toFixed(2)}`}
</button>
</div>
)}

{showHirerPickupChecklist && (
<div style={{ margin: '8px 16px', background: '#FFF8E7', border: '2px solid #F5C842', borderRadius: 12, padding: '14px 16px', flexShrink: 0 }}>
<div style={{ fontWeight: 700, marginBottom: 4, color: '#8a6d00' }}>📦 Pickup checklist</div>
<div style={{ fontSize: 13, color: '#8a6d00', marginBottom: 12, lineHeight: 1.5 }}>
Before you take the item, please record its condition with a photo. This protects you if there's a dispute later.{activeConvo.listings?.requires_id && <><br /><br /><strong>🪪 Remember: this owner requires your driver's licence at pickup.</strong></>}
</div>
<div style={{ fontSize: 12, fontWeight: 600, color: '#8a6d00', marginBottom: 6 }}>1. Select item condition:</div>
<div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
<div onClick={() => setPickupCondition('good')} style={conditionOptionStyle(pickupCondition === 'good', '#16A34A')}>✅ Good</div>
<div onClick={() => setPickupCondition('minor wear')} style={conditionOptionStyle(pickupCondition === 'minor wear', '#F59E0B')}>🟡 Minor wear</div>
<div onClick={() => setPickupCondition('damaged')} style={conditionOptionStyle(pickupCondition === 'damaged', '#DC2626')}>⚠️ Damaged</div>
</div>
<div style={{ fontSize: 12, fontWeight: 600, color: '#8a6d00', marginBottom: 6 }}>2. Take or upload a photo:</div>
{photoError && <div className="alert alert-error" style={{ marginBottom: 8, fontSize: 12 }}>{photoError}</div>}
<input ref={pickupPhotoRef} type="file" accept="image/*" capture="environment" onChange={handlePickupPhotoUpload} style={{ display: 'none' }} />
<button
onClick={() => {
if (!pickupCondition) { setPhotoError('Please select condition first.'); return }
setPhotoError('')
pickupPhotoRef.current?.click()
}}
className="btn btn-full"
style={{ background: '#8a6d00', color: 'white' }}
disabled={photoUploading}
>
{photoUploading ? '📸 Uploading...' : '📸 Take pickup photo'}
</button>
</div>
)}

{showOwnerPickupVerify && (
<div style={{ margin: '8px 16px', background: '#FFF8E7', border: '2px solid #F5C842', borderRadius: 12, padding: '14px 16px', flexShrink: 0 }}>
<div style={{ fontWeight: 700, marginBottom: 4, color: '#8a6d00' }}>📦 Pickup verification (optional)</div>
<div style={{ fontSize: 13, color: '#8a6d00', marginBottom: 12, lineHeight: 1.5 }}>
The hirer has picked up your item and recorded its condition as <strong>{activeConvo.pickup_condition}</strong>. You can optionally take your own photo for extra protection.
</div>
{photoError && <div className="alert alert-error" style={{ marginBottom: 8, fontSize: 12 }}>{photoError}</div>}
<input ref={ownerPickupPhotoRef} type="file" accept="image/*" capture="environment" onChange={handleOwnerPickupPhotoUpload} style={{ display: 'none' }} />
<div style={{ display: 'flex', gap: 8 }}>
<button
onClick={() => { setPhotoError(''); ownerPickupPhotoRef.current?.click() }}
className="btn"
style={{ background: '#8a6d00', color: 'white', flex: 1 }}
disabled={photoUploading}
>
📸 Take my photo
</button>
<button
onClick={handleOwnerPickupSkip}
className="btn btn-ghost"
disabled={photoUploading}
style={{ flex: 1 }}
>
Skip
</button>
</div>
</div>
)}

{showHirerReturnChecklist && (
<div style={{ margin: '8px 16px', background: 'var(--accent-light)', border: '2px solid var(--accent)', borderRadius: 12, padding: '14px 16px', flexShrink: 0 }}>
<div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--accent)' }}>📦 Return checklist</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
Returning the item? Take a photo to confirm its condition. The owner will then confirm and release your deposit.
</div>
<div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 6 }}>1. Item condition at return:</div>
<div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
<div onClick={() => setReturnCondition('good')} style={conditionOptionStyle(returnCondition === 'good', '#16A34A')}>✅ Good</div>
<div onClick={() => setReturnCondition('minor wear')} style={conditionOptionStyle(returnCondition === 'minor wear', '#F59E0B')}>🟡 Minor wear</div>
<div onClick={() => setReturnCondition('damaged')} style={conditionOptionStyle(returnCondition === 'damaged', '#DC2626')}>⚠️ Damaged</div>
</div>
<div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 6 }}>2. Take or upload a photo:</div>
{photoError && <div className="alert alert-error" style={{ marginBottom: 8, fontSize: 12 }}>{photoError}</div>}
<input ref={returnPhotoRef} type="file" accept="image/*" capture="environment" onChange={handleReturnPhotoUpload} style={{ display: 'none' }} />
<button
onClick={() => {
if (!returnCondition) { setPhotoError('Please select condition first.'); return }
setPhotoError('')
returnPhotoRef.current?.click()
}}
className="btn btn-full"
style={{ background: 'var(--accent)', color: 'white' }}
disabled={photoUploading}
>
{photoUploading ? '📸 Uploading...' : '📸 Take return photo'}
</button>
</div>
)}

{showOwnerReturnVerify && (
<div style={{ margin: '8px 16px', background: 'var(--accent-light)', border: '2px solid var(--accent)', borderRadius: 12, padding: '14px 16px', flexShrink: 0 }}>
<div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--accent)' }}>📦 Confirm item return — required</div>
<div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
The hirer says they've returned your item with condition: <strong>{activeConvo.return_condition}</strong>. Please confirm — this releases their deposit.
</div>
{activeConvo.return_photo_url && (
<img src={activeConvo.return_photo_url} alt="Hirer return" loading="lazy" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, marginBottom: 10 }} />
)}

<div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 6 }}>Optional: Take your own return photo</div>
{photoError && <div className="alert alert-error" style={{ marginBottom: 8, fontSize: 12 }}>{photoError}</div>}
<input ref={ownerReturnPhotoRef} type="file" accept="image/*" capture="environment" onChange={handleOwnerReturnPhotoUpload} style={{ display: 'none' }} />
<button
onClick={() => { setPhotoError(''); ownerReturnPhotoRef.current?.click() }}
className="btn btn-ghost btn-full"
disabled={photoUploading}
style={{ marginBottom: 12, border: '1px solid var(--accent)', color: 'var(--accent)' }}
>
📸 Take my own photo
</button>

<div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 6 }}>Notes (optional):</div>
<textarea
className="form-control"
value={ownerReturnNotes}
onChange={e => setOwnerReturnNotes(e.target.value)}
placeholder="Anything to note about the item's return condition..."
style={{ minHeight: 60, marginBottom: 12, fontSize: 13 }}
/>

<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
<button
onClick={() => handleOwnerReturnAgree(true)}
className="btn"
style={{ background: '#16A34A', color: 'white', flex: '1 1 140px', minWidth: 0 }}
disabled={photoUploading}
>
✅ Agree — Release deposit
</button>
<button
onClick={() => handleOwnerReturnAgree(false)}
className="btn"
style={{ background: '#DC2626', color: 'white', flex: '1 1 140px', minWidth: 0 }}
disabled={photoUploading}
>
⚠️ Disagree — Open dispute
</button>
</div>
</div>
)}

{activeConvo.returned_at && isHirer(activeConvo) && activeConvo.owner_return_agreed === null && (
<div style={{ margin: '8px 16px', background: '#E8F8EE', border: '1px solid #16A34A', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#15803D', flexShrink: 0 }}>
✅ Return photo uploaded · Waiting for owner to confirm
</div>
)}
{activeConvo.owner_return_agreed === true && (
<div style={{ margin: '8px 16px', background: '#E8F8EE', border: '1px solid #16A34A', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#15803D', flexShrink: 0 }}>
✅ Return confirmed by owner · Booking complete · Deposit released
</div>
)}
{activeConvo.owner_return_agreed === false && (
<div style={{ margin: '8px 16px', background: '#FEE2E2', border: '1px solid #DC2626', borderRadius: 12, padding: '12px 16px', fontSize: 13, color: '#991B1B', flexShrink: 0 }}>
⚠️ Owner disputes return condition · HireIt team reviewing
</div>
)}

{isBlocked && (
<div className="alert alert-warn" style={{ margin: '8px 16px', fontSize: 13, flexShrink: 0 }}>
You have blocked this user. Unblock them to send messages.
</div>
)}

<div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
{messages.length === 0 ? (
<div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 14, marginTop: '2rem' }}>
No messages yet. Say hello! 👋
</div>
) : (
(() => {
const formatDayLabel = (d) => {
const now = new Date()
const y = new Date(now); y.setDate(now.getDate() - 1)
const sameDay = (a, b) => a.toDateString() === b.toDateString()
if (sameDay(d, now)) return 'Today'
if (sameDay(d, y)) return 'Yesterday'
return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
}
return messages.map((msg, i, arr) => {
const isMe = msg.sender_id === user.id
const isLatestMine = isMe && msg.id === latestMyMessageId
const photoMatch = msg.body?.match(/(https?:\/\/[^\s]+\.(jpg|jpeg|png|webp))/i)
const photoUrl = photoMatch ? photoMatch[1] : null
const textBody = photoUrl ? msg.body.replace(photoUrl, '').trim() : msg.body
const msgDate = new Date(msg.created_at)
const prevDate = i > 0 ? new Date(arr[i - 1].created_at) : null
const showDayLabel = !prevDate || msgDate.toDateString() !== prevDate.toDateString()
return (
<Fragment key={`row-${msg.id}`}>
{showDayLabel && (
<div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-muted)', margin: '12px 0 4px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
{formatDayLabel(msgDate)}
</div>
)}
<div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
<div style={{
maxWidth: '70%', padding: '10px 14px',
borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
background: isMe ? 'var(--primary)' : 'var(--gray-100)',
color: isMe ? 'white' : 'var(--text)', fontSize: 14, lineHeight: 1.5,
}}>
{!isMe && <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4, color: 'var(--primary)' }}>{msg.sender?.full_name}</div>}
<div style={{ whiteSpace: 'pre-wrap' }}>{textBody}</div>
{photoUrl && (
<img src={photoUrl} alt="Booking" loading="lazy" style={{ maxWidth: '100%', borderRadius: 8, marginTop: 6, cursor: 'pointer' }} onClick={() => window.open(photoUrl, '_blank')} />
)}
<div style={{ fontSize: 11, opacity: 0.7, marginTop: 4, textAlign: isMe ? 'right' : 'left' }}>
{new Date(msg.created_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
</div>
</div>
{isLatestMine && (
<div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2, marginRight: 4, fontWeight: 500 }}>
{msg.read ? '✓✓ Read' : '✓ Sent'}
</div>
)}
</div>
</Fragment>
)
})
})()
)}

{otherTyping && (
<div style={{ display: 'flex', justifyContent: 'flex-start' }}>
<div style={{
padding: '10px 14px',
borderRadius: '16px 16px 16px 4px',
background: 'var(--gray-100)',
color: 'var(--text-muted)',
fontSize: 13,
fontStyle: 'italic',
display: 'flex',
alignItems: 'center',
gap: 6,
}}>
{otherPerson?.full_name?.split(' ')[0]} is typing
<span className="typing-dots">
<span style={{ animation: 'typingDot 1.4s infinite', animationDelay: '0s' }}>·</span>
<span style={{ animation: 'typingDot 1.4s infinite', animationDelay: '0.2s' }}>·</span>
<span style={{ animation: 'typingDot 1.4s infinite', animationDelay: '0.4s' }}>·</span>
</span>
</div>
</div>
)}
<div ref={messagesEndRef} />
</div>
</div>

{showQuickReplies && (
<div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', background: 'var(--gray-50)', overflowX: 'auto', whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch', flexShrink: 0 }}>
<div style={{ display: 'inline-flex', gap: 6 }}>
{quickReplies.map((reply, i) => (
<button
key={i}
onClick={() => handleQuickReply(reply)}
disabled={sending}
style={{
background: reply.instant ? '#E8DEF5' : 'white',
color: reply.instant ? '#5B2DB5' : 'var(--navy)',
border: reply.instant ? '1px solid #C5A8E8' : '1px solid var(--border-strong)',
borderRadius: 99,
padding: '6px 12px',
fontSize: 13,
fontWeight: 500,
cursor: 'pointer',
whiteSpace: 'nowrap',
transition: 'all 0.15s',
flexShrink: 0,
}}
>
{reply.text}
</button>
))}
</div>
</div>
)}

{!activeConvo.cancelled_at && (
<div style={{ padding: '6px 12px', borderTop: '1px solid var(--border)', background: 'var(--gray-50)', display: 'flex', gap: 4, flexShrink: 0, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
{showCancelButton && (
<button onClick={() => setCancelModal(true)} style={{ fontSize: 11, color: 'var(--red)', background: 'transparent', border: 'none', padding: '4px 8px', cursor: 'pointer', fontWeight: 500 }}>❌ Cancel booking</button>
)}
{isBlocked ? (
<button onClick={handleUnblock} style={{ fontSize: 11, color: 'var(--text-muted)', background: 'transparent', border: 'none', padding: '4px 8px', cursor: 'pointer', fontWeight: 500 }}>🔓 Unblock</button>
) : (
<button onClick={() => setBlockConfirm(true)} style={{ fontSize: 11, color: 'var(--text-muted)', background: 'transparent', border: 'none', padding: '4px 8px', cursor: 'pointer', fontWeight: 500 }}>🚫 Block</button>
)}
<button onClick={() => setReportModal(true)} style={{ fontSize: 11, color: 'var(--text-muted)', background: 'transparent', border: 'none', padding: '4px 8px', cursor: 'pointer', fontWeight: 500 }}>⚠️ Report</button>
</div>
)}

<input ref={chatPhotoRef} type="file" accept="image/*" capture="environment" onChange={handleChatPhotoUpload} style={{ display: 'none' }} />
<div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
<button
onClick={() => { if (!isBlocked && !activeConvo.cancelled_at) chatPhotoRef.current?.click() }}
disabled={isBlocked || photoUploading || !!activeConvo.cancelled_at}
style={{
background: 'var(--gray-100)',
border: 'none',
borderRadius: '50%',
width: 40,
height: 40,
cursor: (isBlocked || activeConvo.cancelled_at) ? 'not-allowed' : 'pointer',
fontSize: 18,
flexShrink: 0,
opacity: (isBlocked || activeConvo.cancelled_at) ? 0.4 : 1,
}}
title="Send photo"
>
📷
</button>
<input
ref={inputRef}
className="form-control"
placeholder={isBlocked ? 'You have blocked this user' : activeConvo.cancelled_at ? 'Booking cancelled' : 'Type a message...'}
value={newMessage}
onChange={handleInputChange}
onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
style={{ flex: 1 }}
disabled={isBlocked || !!activeConvo.cancelled_at}
/>
<button className="btn btn-gradient" onClick={sendMessage} disabled={sending || !newMessage.trim() || isBlocked || !!activeConvo.cancelled_at}>
{sending ? '...' : 'Send'}
</button>
</div>
</>
)}
</div>
)}
</div>
</div>

<style>{`
@keyframes typingDot {
0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
30% { opacity: 1; transform: translateY(-3px); }
}
.typing-dots span {
display: inline-block;
font-size: 18px;
line-height: 1;
}
`}</style>

{cancelModal && refundPreview && (
<div className="modal-overlay" onClick={() => setCancelModal(false)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Cancel this booking?</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
Once cancelled, this booking cannot be reinstated.
</p>

<div style={{ background: refundPreview.policy === 'unpaid' ? 'var(--gray-50)' : refundPreview.feeAmount > 0 ? '#FFF8E7' : '#E8F8EE', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, lineHeight: 1.6 }}>
{refundPreview.policy === 'unpaid' && (
<>
<div style={{ fontWeight: 700, marginBottom: 4 }}>💸 No payment yet</div>
<div>You haven't been charged. Cancelling is free.</div>
</>
)}
{refundPreview.policy === 'owner-cancel' && (
<>
<div style={{ fontWeight: 700, marginBottom: 4, color: '#15803D' }}>💰 Owner cancellation — full refund</div>
<div>Hirer will receive a full refund of <strong>${refundPreview.refundAmount.toFixed(2)}</strong> within 3-5 business days.</div>
</>
)}
{refundPreview.policy === '48+hr' && (
<>
<div style={{ fontWeight: 700, marginBottom: 4, color: '#15803D' }}>💰 Full refund — 48+ hours notice</div>
<div>You'll receive <strong>${refundPreview.refundAmount.toFixed(2)}</strong> back within 3-5 business days.</div>
</>
)}
{refundPreview.policy === '24-48hr' && (
<>
<div style={{ fontWeight: 700, marginBottom: 4, color: '#8a6d00' }}>⚠️ 50% refund — 24-48 hours notice</div>
<div>Refund: <strong>${refundPreview.refundAmount.toFixed(2)}</strong> · Cancellation fee: <strong>${refundPreview.feeAmount.toFixed(2)}</strong></div>
</>
)}
{refundPreview.policy === 'under-24hr' && (
<>
<div style={{ fontWeight: 700, marginBottom: 4, color: '#991B1B' }}>⚠️ no refund — under 24 hours notice</div>
<div>Refund: <strong>${refundPreview.refundAmount.toFixed(2)}</strong> · Cancellation fee: <strong>${refundPreview.feeAmount.toFixed(2)}</strong></div>
</>
)}
</div>

<div className="form-group">
<label className="form-label">Reason (optional, visible to other party)</label>
<textarea
className="form-control"
value={cancelReason}
onChange={e => setCancelReason(e.target.value)}
placeholder="e.g. Change of plans, item no longer available..."
style={{ minHeight: 70 }}
/>
</div>

<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16, flexWrap: 'wrap' }}>
<button className="btn btn-ghost" onClick={() => setCancelModal(false)} disabled={cancelling}>Keep booking</button>
<button className="btn btn-danger" onClick={handleCancelBooking} disabled={cancelling}>
{cancelling ? 'Cancelling...' : '❌ Cancel booking'}
</button>
</div>
</div>
</div>
)}

{showPaymentWarning && (
<div className="modal-overlay" onClick={() => setShowPaymentWarning(false)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Payment Warning</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>
Your message appears to reference an off-platform payment method. For your protection, all payments must be processed through HireIt. Off-platform transactions are not covered by HireIt dispute resolution or refund policy.
</p>
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
<button className="btn btn-ghost" onClick={() => setShowPaymentWarning(false)}>Edit message</button>
<button className="btn btn-danger" onClick={sendMessageAnyway}>Send anyway</button>
</div>
</div>
</div>
)}

{showContactWarning && (
<div className="modal-overlay" onClick={() => setShowContactWarning(false)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>🔒 Keep it on HireIt</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>
{CONTACT_WARNING} Your message looks like it includes a phone number, email, social media or a link. Messages on HireIt keep a record if anything goes wrong.
</p>
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
<button className="btn btn-ghost" onClick={() => setShowContactWarning(false)}>Edit message</button>
{CONTACT_MODE !== 'block' && <button className="btn btn-danger" onClick={sendContactAnyway}>Send anyway</button>}
</div>
</div>
</div>
)}

{blockConfirm && (
<div className="modal-overlay" onClick={() => setBlockConfirm(false)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Block {otherPerson?.full_name}?</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 20 }}>
They won't be able to message you. You can unblock them at any time.
</p>
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
<button className="btn btn-ghost" onClick={() => setBlockConfirm(false)}>Cancel</button>
<button className="btn btn-danger" onClick={handleBlock}>Block user</button>
</div>
</div>
</div>
)}

{reportModal && (
<div className="modal-overlay" onClick={() => setReportModal(false)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<h3 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--navy)' }}>Report {otherPerson?.full_name}</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 16 }}>
Tell us what happened. Our team will review this report.
</p>
<div className="form-group">
<label className="form-label">Reason for report</label>
<textarea
className="form-control"
value={reportReason}
onChange={e => setReportReason(e.target.value)}
placeholder="Describe the abusive or inappropriate behaviour..."
style={{ minHeight: 100 }}
/>
</div>
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
<button className="btn btn-ghost" onClick={() => setReportModal(false)}>Cancel</button>
<button className="btn btn-danger" onClick={handleReport} disabled={!reportReason.trim()}>Submit report</button>
</div>
</div>
</div>
)}
</div>
)
}


